import React from "react";
import { ProviderManifest } from "../../services/api";
import { StrategyProviders } from "../../types/strategies";

export type LatencyComparisonProviders = Partial<Record<"stt" | "llm" | "tts" | "v2v", string[]>>;

type ProviderServiceKey = "stt" | "llm" | "tts";

const serviceLabels: Record<ProviderServiceKey, string> = {
  stt: "STT",
  llm: "LLM",
  tts: "TTS",
};

const serviceDescriptions: Record<ProviderServiceKey, string> = {
  stt: "Speech recognition comparison",
  llm: "Model response comparison",
  tts: "Voice response comparison",
};

const LatencyComparisonProvidersCard: React.FC<{
  manifest: ProviderManifest | null;
  currentProviders: StrategyProviders;
  comparisonProviders: LatencyComparisonProviders;
  onChange: (comparisonProviders: LatencyComparisonProviders) => void;
}> = ({ manifest, currentProviders, comparisonProviders, onChange }) => {
  const updateProvider = (service: ProviderServiceKey, providerId: string, checked: boolean) => {
    const currentValues = comparisonProviders[service] || [];
    const nextValues = checked
      ? Array.from(new Set([...currentValues, providerId]))
      : currentValues.filter((item) => item !== providerId);

    onChange({
      ...comparisonProviders,
      [service]: nextValues,
    });
  };

  return (
    <div className="bg-[#f2efe3] p-6 rounded-lg border border-[#e7e2d3]">
      <h3 className="text-lg font-medium mb-1">Latency Comparison Providers</h3>
      <p className="text-xs text-[#6c6c6c] mb-4">
        Compare alternatives before each outbound call. If a provider is healthy and at least 15% faster, that call can use it.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {(Object.keys(serviceLabels) as ProviderServiceKey[]).map((service) => {
          const providers = manifest?.[service] || [];
          const currentProvider = currentProviders[service];
          const selected = new Set(comparisonProviders[service] || []);

          return (
            <div key={service} className="bg-white border border-[#e7e2d3] rounded-lg p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-[#140d0c]">{serviceLabels[service]}</div>
                  <div className="text-xs text-[#6c6c6c] mt-1">{serviceDescriptions[service]}</div>
                </div>
                {currentProvider && (
                  <span className="rounded-full bg-[#f7f4e8] px-2 py-0.5 text-[11px] text-[#6c6c6c]">
                    Current: {currentProvider}
                  </span>
                )}
              </div>

              <div className="mt-3 space-y-2">
                {providers.length > 0 ? (
                  providers.map((provider) => {
                    const isCurrent = provider.id === currentProvider;
                    return (
                      <label
                        key={provider.id}
                        className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${
                          isCurrent ? "border-[#eee8d8] bg-[#fbfaf5] text-[#9a948b]" : "border-[#eee8d8] bg-white text-[#140d0c]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(provider.id)}
                          disabled={isCurrent}
                          onChange={(event) => updateProvider(service, provider.id, event.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#ffcc33] focus:ring-[#ffcc33]"
                        />
                        <span>
                          <span className="block font-medium">{provider.label || provider.id}</span>
                          <span className="block text-xs text-[#6c6c6c]">{isCurrent ? "Current configured provider" : provider.id}</span>
                        </span>
                      </label>
                    );
                  })
                ) : (
                  <div className="rounded-md border border-[#eee8d8] bg-[#fbfaf5] p-3 text-sm text-[#6c6c6c]">
                    No providers available.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LatencyComparisonProvidersCard;
